import { rfBuyingType } from 'src/const/state/ad';
import * as types from 'src/store/types/index';
import numeral from 'numeral';
import { AudienceType } from 'src/const/state/update-audience';
import stockEstimate from './stock-estimate';


export default {
  namespaced: true,
  modules: {
    stockEstimate,
  },
  state: {
    // 库存预估数据
    estimateData: {
      rf_total_result: {},
      rf_daily_result: {},
      rf_impression: {
        categories: [],
        series: [],
      },
      rf_frequency_per_person: {},
    },
    // 订单库存信息
    stockInfo: {
      rf_predict_impression: 0,
      rf_predict_reach: 0,
      rf_predict_cpr: 0,
      rf_predict_frequency: 0,
      budget_mode: 1, // R&F只支持 budget_mode=TOTAL 总预算
      budget: '',
      ad_reach_frequency_buy_type: rfBuyingType.fixedBudget, // 0固定预算，1固定展示量
      rf_predict_percentage: 0,
      rf_estimate_key: '0',
      rf_estimate_info: null,
    },
    // 不同购买方式下的输入值
    buyTypeValues: {
      [rfBuyingType.fixedBudget]: 0,
      [rfBuyingType.fixedImpression]: 0,
      [rfBuyingType.fixedReachPercent]: 0,
      [rfBuyingType.fixedReach]: 0,
    },
    // 库存相关参数表单校验是否通过
    isValidTargetingParams: true,
    // 编辑时是否库存变化
    isEditEstimtateChanged: false,
    // rf人群包原始包id列表
    audienceTagsMap: {},
    originalRetargetingTags: [],
    originalRetargetingTagsExclude: [],
    /** rf 下是否展示 13-17 的年龄选项 */
    isRfBelow18AgeButtonVisible: false,
    draftCreatives: [],
    frequency: '',
    frequencySchedule: '',
  },
  getters: {
    // 购买方式
    buyType(state) {
      return state.stockInfo.ad_reach_frequency_buy_type;
    },
    // 当前购买方式下的输入值
    buyTypeValue(state, getters) {
      if (!getters.buyType && getters.buyType !== 0) return '';
      return state.buyTypeValues[getters.buyType];
    },
    // 可选预算区间
    budgetThreshold(state) {
      const { categories } = state.estimateData.rf_impression;
      if (!categories) return [0, Number.MAX_SAFE_INTEGER];
      return [categories[0], categories[categories.length - 1]];
    },
    // 最大uv
    maxReach(state) {
      return state.estimateData.rf_total_result.max_uv_mille || state.stockInfo.rf_estimate_info?.max_uv_mille;
    },
    // 可选pv区间
    impressionThreshold(state) {
      const { series } = state.estimateData.rf_impression;
      const impressionData = series.find(item => item.name === 'impression')?.data;
      if (!impressionData) return [0, Number.MAX_SAFE_INTEGER];
      return [impressionData[0], impressionData[impressionData.length - 1]];
    },
    // 可选uv区间
    reachThreshold(state, getters) {
      const { series } = state.estimateData.rf_impression;
      const reachData = series.find(item => item.name === 'reach')?.data;
      if (!reachData) return [0, Number.MAX_SAFE_INTEGER];
      const max = state.estimateData.rf_total_result.uv;
      return [reachData[0], max];
    },
    // 可选uv百分比区间
    reachPercentThreshold(state, getters) {
      const { series } = state.estimateData.rf_impression;
      const reachData = series.find(item => item.name === 'reach')?.data;
      if (!reachData) return [0, 100];
      const maxReach = getters.maxReach || reachData[reachData.length - 1];
      const minValue = reachData[0] / maxReach * 100;
      const min = Math.floor(minValue * 100 + 1) / 100;
      const max = numeral(state.estimateData.rf_total_result.uv / maxReach * 100).format('0.000').slice(0, -1);
      return [min, max];
    },
    // 当前购买方式下可选值区间
    buyTypeThreshold(state, getters) {
      switch (getters.buyType) {
        case rfBuyingType.fixedBudget: return getters.budgetThreshold;
        case rfBuyingType.fixedImpression: return getters.impressionThreshold;
        case rfBuyingType.fixedReach: return getters.reachThreshold;
        case rfBuyingType.fixedReachPercent: return getters.reachPercentThreshold;
        default: break;
      }
    },
    // 当前购买方式下最小购买值
    minBuyTypeValue(state, getters) {
      const range = getters.buyTypeThreshold;
      return range[0];
    },
    // 当前购买方式下最大购买值
    maxBuyTypeValue(state, getters) {
      const range = getters.buyTypeThreshold;
      return range[1];
    },
    // 当前输入值是否在可选区间外
    outOfValueRange(state, getters) {
      return +getters.buyTypeValue < +getters.minBuyTypeValue || +getters.buyTypeValue > +getters.maxBuyTypeValue;
    },
    // 调整的目标区间端点值
    targetVal(state, getters) {
      if (+getters.buyTypeValue < +getters.minBuyTypeValue) {
        return getters.minBuyTypeValue;
      }
      if (+getters.buyTypeValue > +getters.maxBuyTypeValue) {
        return getters.maxBuyTypeValue;
      }
    },
    getOriginalTagsByShadow(state) {
      return (tags) => {
        if (!tags || tags.length === 0) return [];
        const keys = Object.keys(state.audienceTagsMap.retargeting_tags_map ?? {}).map(e => +e);
        const values = Object.values(state.audienceTagsMap.retargeting_tags_map ?? {}).map(e => +e);
        return tags.map((tag) => {
          const index = keys.findIndex(e => e === tag);
          if (index !== -1) {
            return values[index];
          }
          return tag;
        });
      };
    },
    getShadowTagsByOriginal(state) {
      return (tags, type) => {
        if (!tags || tags.length === 0) return [];
        const keys = Object.keys(state.audienceTagsMap.retargeting_tags_map ?? {}).map(e => +e);
        const values = Object.values(state.audienceTagsMap.retargeting_tags_map ?? {}).map(e => +e);
        const originalTags = type === AudienceType.INCLUDE ? state.originalRetargetingTags : state.originalRetargetingTagsExclude;
        return tags.map((tag) => {
          const index = values.findIndex(e => e === tag);
          if (index !== -1 && originalTags.includes(tag)) {
            return keys[index];
          }
          return tag;
        });
      };
    },
    isShadowAudience(state) {
      return (id, type) => {
        const list = type === AudienceType.INCLUDE ? state.originalRetargetingTags : state.originalRetargetingTagsExclude;
        return list.includes(id);
      };
    },
    getShadowName(state) {
      return (id) => {
        const { retargeting_tags_map, retargeting_tags_name_map } = state.audienceTagsMap;
        if (!retargeting_tags_map || !retargeting_tags_name_map) {
          return;
        }
        let shadowId;
        Object.entries(retargeting_tags_map).forEach(([sKey, sValue]) => {
          if (+sValue === id) {
            shadowId = +sKey;
          }
        });
        if (!shadowId) return;
        return retargeting_tags_name_map[shadowId];
      };
    },
  },
  mutations: {
    [types.SET_STOCK_INFO](state, info = {}) {
      const { stockInfo } = state;
      const fields = Object.keys(stockInfo) || [];
      if (fields && fields.length) {
        // 根据返回uv值及最大uv初始化uv百分比   uv=reach
        if (info.rf_estimate_info && !stockInfo.rf_predict_percentage) {
          info.rf_predict_percentage = numeral(info.rf_predict_reach / info.rf_estimate_info.max_uv_mille * 100).format('0.000').slice(0, -1);
        }
        for (const field of fields) {
          if (info && Object.prototype.hasOwnProperty.call(info, field)) {
            stockInfo[field] = info[field];
          }
        }
      }
    },
    [types.SET_ESTIMATE_INFO](state, info = {}) {
      state.estimateData = { ...state.estimateData, ...info };
    },
    [types.SET_BUYTYPE_VALUE](state, { type, value }) {
      state.buyTypeValues[type] = value;
      switch (type) {
        case rfBuyingType.fixedBudget: state.stockInfo.budget = value;
          break;
        case rfBuyingType.fixedImpression: state.stockInfo.rf_predict_impression = value;
          break;
        case rfBuyingType.fixedReach: state.stockInfo.rf_predict_reach = value;
          break;
        case rfBuyingType.fixedReachPercent: state.stockInfo.rf_predict_percentage = value;
          break;
        default: break;
      }
    },
    [types.INIT_RF_INFO](state) {
      state.estimateData = {
        rf_total_result: {},
        rf_daily_result: {},
        rf_impression: {
          categories: [],
          series: [],
        },
        rf_frequency_per_person: {},
      };
      state.stockInfo = {
        rf_predict_impression: 0,
        rf_predict_reach: 0,
        rf_predict_cpr: 0,
        rf_predict_frequency: 0,
        budget_mode: 1,
        budget: '',
        ad_reach_frequency_buy_type: rfBuyingType.fixedBudget,
        rf_predict_percentage: 0,
        rf_estimate_key: '0',
        rf_estimate_info: null,
      };
    },
    [types.SET_VALIDATE_TARGETING_PARAMS_STATUS](state, status) {
      state.isValidTargetingParams = status;
    },
    [types.SET_RF_BELOW18AGEBUTTONVISIBLE](state, list) {
      state.isRfBelow18AgeButtonVisible = list;
    },
    [types.SET_RF_ISEDITESTIMATECHANGED](state, status) {
      state.isEditEstimtateChanged = status;
    },
    [types.SET_RF_ORIGINALAUDIENCETAGS](state, list) {
      state.originalRetargetingTags = list;
    },
    [types.SET_RF_ORIGINALAUDIENCETAGS_EXCLUDE](state, list) {
      state.originalRetargetingTagsExclude = list;
    },
    [types.SET_RF_AUDIENCETAGMAP](state, map) {
      state.audienceTagsMap = map;
    },
    [types.SET_RF_DRAFTCREATIVES](state, creatives) {
      state.draftCreatives = creatives;
    },
    [types.SET_RF_FREQUENCY](state, frequency) {
      state.frequency = frequency;
    },
    [types.SET_RF_FREQUENCY_SCHEDULE](state, frequencySchedule) {
      state.frequencySchedule = frequencySchedule;
    },
  },
  actions: {
    setRfBelow18AgeButtonVisible({ commit }, visible) {
      commit(types.SET_RF_BELOW18AGEBUTTONVISIBLE, visible);
    },
  },
};
